/**
 * Product Carousel - Swiper.js Initialization
 *
 * Initializes Swiper carousels for product carousel blocks
 * @package twentytwentyfour-child
 */

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Expose Swiper globally for inline scripts
window.Swiper = Swiper;
window.SwiperModules = { Navigation, Pagination, Autoplay };

/**
 * Initialize a product carousel
 * @param {string} carouselId - Unique ID for this carousel instance
 * @param {object} options - Swiper configuration options
 */
window.initProductCarousel = function(carouselId, options = {}) {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            initCarousel(carouselId, options);
        });
    } else {
        initCarousel(carouselId, options);
    }
};

/**
 * Internal function to initialize the carousel
 */
function initCarousel(carouselId, options) {
    const carouselElement = document.querySelector('.' + carouselId);

    if (!carouselElement) {
        console.warn(`Product carousel element not found: .${carouselId}`);
        return;
    }

    // Default Swiper configuration
    const defaultConfig = {
        modules: [Navigation, Pagination, Autoplay],
        slidesPerView: 'auto',
        spaceBetween: 20,
        loop: false,
        grabCursor: true,
        watchOverflow: true,
        breakpoints: {
            640: {
                slidesPerView: 2,
                spaceBetween: 20,
            },
            768: {
                slidesPerView: 2,
                spaceBetween: 20,
            },
            1024: {
                slidesPerView: 3,
                spaceBetween: 30,
            },
        },
    };

    // Merge user options with defaults
    const config = { ...defaultConfig, ...options };

    // Add autoplay configuration if enabled
    if (config.autoplay === true) {
        config.autoplay = {
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
        };
    }

    // Initialize Swiper
    const swiper = new Swiper(carouselElement, config);

    // Set up custom navigation buttons
    const prevButton = document.querySelector(`.swiper-button-prev-custom[data-carousel="${carouselId}"]`);
    const nextButton = document.querySelector(`.swiper-button-next-custom[data-carousel="${carouselId}"]`);

    if (prevButton && nextButton) {
        prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            swiper.slidePrev();
        });

        nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            swiper.slideNext();
        });

        // Update button states
        swiper.on('slideChange', () => {
            updateNavigationButtons(swiper, prevButton, nextButton);
        });

        // Initial button state
        updateNavigationButtons(swiper, prevButton, nextButton);
    }

    // Store swiper instance for potential external access
    carouselElement.swiper = swiper;

    return swiper;
}

/**
 * Update navigation button states (disabled/enabled)
 */
function updateNavigationButtons(swiper, prevButton, nextButton) {
    if (swiper.isBeginning) {
        prevButton.setAttribute('disabled', 'disabled');
        prevButton.style.opacity = '0.3';
        prevButton.style.cursor = 'not-allowed';
    } else {
        prevButton.removeAttribute('disabled');
        prevButton.style.opacity = '1';
        prevButton.style.cursor = 'pointer';
    }

    if (swiper.isEnd) {
        nextButton.setAttribute('disabled', 'disabled');
        nextButton.style.opacity = '0.3';
        nextButton.style.cursor = 'not-allowed';
    } else {
        nextButton.removeAttribute('disabled');
        nextButton.style.opacity = '1';
        nextButton.style.cursor = 'pointer';
    }
}
